import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, track, syncSaved } from "@/lib/api";
import { translate } from "@/lib/i18n";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

const browserLang = () => {
  const l = (navigator.language || "").slice(0, 2).toLowerCase();
  return l === "fr" || l === "en" ? l : null;
};

export function AppProvider({ children }) {
  const [meta, setMeta] = useState({ hubs: [], areas: [], categories: [] });
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [hubId, setHubId] = useState(localStorage.getItem("dm_hub") || "");
  const [manualLang, setManualLang] = useState(localStorage.getItem("dm_lang_manual"));
  const [auth, setAuth] = useState(null);
  const [unread, setUnread] = useState(0);

  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem("dm_token")) { setUser(null); return null; }
    try {
      const { data } = await api.get("/me");
      await syncSaved(false);
      setUser(data); setUnread(data.unread || 0);
      return data;
    } catch { localStorage.removeItem("dm_token"); setUser(null); return null; }
  }, []);

  useEffect(() => {
    Promise.all([api.get("/meta").then((r) => setMeta(r.data)), refreshUser()]).finally(() => setReady(true));
  }, [refreshUser]);

  const hub = meta.hubs.find((h) => h.id === (hubId || user?.last_hub_id || user?.city_hub_id)) || meta.hubs[0];
  const userArea = meta.areas.find((a) => a.id === user?.area_id);
  const side = userArea?.side || localStorage.getItem("dm_side");
  const hubLang = hub?.default_language || (side === "gatineau" ? "fr" : "en");
  const lang = manualLang || user?.preferred_language || browserLang() || hubLang || "en";

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const t = useCallback((k, v) => translate(lang, k, v), [lang]);
  const L = useCallback((obj) => (obj ? (lang === "fr" ? obj.name_fr : obj.name_en) : ""), [lang]);

  const setLang = (l) => {
    localStorage.setItem("dm_lang_manual", l); setManualLang(l);
    track("language_toggled", { meta: { lang: l } });
    if (user) api.put("/me", { preferred_language: l }).catch(() => {});
  };
  const selectHub = (id, source = "manual") => {
    localStorage.setItem("dm_hub", id); setHubId(id);
    track("hub_selected", { hub_id: id, meta: { source } });
    if (user) api.put("/me", { last_hub_id: id }).catch(() => {});
  };
  const login = async (token, u) => { localStorage.setItem("dm_token", token); await syncSaved(true); setUser(u); refreshUser(); };
  const logout = () => { localStorage.removeItem("dm_token"); localStorage.removeItem("dm_saved"); setUser(null); setUnread(0); };

  const requireVerified = () => {
    if (!user) { setAuth({ mode: "prompt" }); track("signup_started"); return false; }
    if (user.is_banned) { toast.error(t("banned")); return false; }
    if (!user.phone_verified) { setAuth({ mode: "verify" }); return false; }
    return true;
  };

  const value = useMemo(() => ({
    meta, user, ready, hub, lang, t, L, setLang, selectHub, login, logout, refreshUser, setUser,
    auth, setAuth, requireVerified, unread, setUnread,
    cat: (id) => meta.categories.find((c) => c.id === id),
    hubOf: (id) => meta.hubs.find((h) => h.id === id),
    areaOf: (id) => meta.areas.find((a) => a.id === id),
    areasOf: (id) => meta.areas.filter((a) => a.hub_id === id),
    activeHubs: meta.hubs.filter((h) => h.launch_status === "active"),
  }), [meta, user, ready, hub, lang, t, L, auth, unread]); // eslint-disable-line react-hooks/exhaustive-deps

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
